import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo, Brain, Sparkles } from '../components/Icons.jsx';
import { Intro, Done, mainStyle } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';

const easyQs = [
  { premises: ['All doctors wear white coats', 'Sarah is a doctor'], q: 'Therefore Sarah wears a white coat. True?', answer: 'true', working: 'Standard valid syllogism: All A are B, X is A → X is B.' },
  { premises: ['Some students are doctors', 'All doctors are professionals'], q: 'Therefore some students are professionals. True?', answer: 'true', working: 'Some A are B, all B are C → some A are C. Valid.' },
  { premises: ['No nurses are surgeons', 'James is a nurse'], q: 'Therefore James is a surgeon. True?', answer: 'false', working: 'No A are B, X is A → X is not B. The conclusion contradicts the premise.' },
  { premises: ['All medics study anatomy', 'Some students study anatomy'], q: 'Therefore some students are medics. True?', answer: 'cannot', working: 'Studying anatomy doesn\'t make someone a medic — other groups also study anatomy. Cannot conclude.' },
  { premises: ['All consultants own a stethoscope', 'Marcus owns a stethoscope'], q: 'Therefore Marcus is a consultant. True?', answer: 'cannot', working: 'Affirming the consequent fallacy. Owning the item doesn\'t prove membership.' },
  { premises: ['Most GPs work in clinics', 'No clinic worker is a paramedic'], q: 'Therefore most GPs are not paramedics. True?', answer: 'true', working: 'If most GPs work in clinics, and no clinic worker is a paramedic, most GPs aren\'t paramedics.' }
];

const hardQs = [
  {
    premises: [
      'All patients in Ward A have hypertension',
      'Some patients with hypertension are also diabetic',
      'No diabetic patient takes drug X',
      'Some patients in Ward A take drug X',
      'Mr Smith is in Ward A'
    ],
    conclusions: [
      { text: 'Mr Smith has hypertension', answer: 'true' },
      { text: 'Mr Smith is diabetic', answer: 'cannot' },
      { text: 'Mr Smith does not take drug X', answer: 'cannot' },
      { text: 'No patient in Ward A is diabetic', answer: 'false' },
      { text: 'Some patients in Ward A are not diabetic', answer: 'true' }
    ],
    working: 'Mr Smith inherits hypertension from being in Ward A. Diabetic status is unknown for him individually. Drug X is taken by some Ward A patients — not all or none. The last conclusion follows because some Ward A patients take drug X and no diabetic does, so those ones aren\'t diabetic.'
  },
  {
    premises: [
      'All medical schools require Chemistry A-level',
      'Some medical schools also require Biology',
      'No school accepts Drama as a third subject for medicine',
      'Cambridge is a medical school',
      'Sarah takes Chemistry, Biology and Drama'
    ],
    conclusions: [
      { text: 'Sarah cannot apply to Cambridge', answer: 'true' },
      { text: 'Sarah takes Chemistry', answer: 'true' },
      { text: 'Cambridge requires Biology', answer: 'cannot' },
      { text: 'All schools that require Biology also require Chemistry', answer: 'true' },
      { text: 'Some schools accept Drama', answer: 'false' }
    ],
    working: 'Cambridge is a medical school → requires Chemistry (Sarah has it). Cambridge may or may not require Biology — Sarah has it either way. But "no school accepts Drama" eliminates her from every medical school, including Cambridge. The last conclusion contradicts the premise.'
  },
  {
    premises: [
      'Some interview candidates score above 700 on UCAT',
      'All candidates scoring above 800 are invited to second-round interviews',
      'No first-round candidate has fewer than 5 work-experience days',
      'Aisha is a first-round candidate',
      'Aisha scored 750 on UCAT'
    ],
    conclusions: [
      { text: 'Aisha has at least 5 work-experience days', answer: 'true' },
      { text: 'Aisha will be invited to second-round interviews', answer: 'cannot' },
      { text: 'All first-round candidates score above 700', answer: 'cannot' },
      { text: 'Aisha scored above 800 on UCAT', answer: 'false' },
      { text: 'No candidate above 800 has fewer than 5 WE days', answer: 'cannot' }
    ],
    working: 'Aisha is first-round → ≥ 5 WE days (true). Her 750 score is below 800, so we don\'t know if she gets second-round (cannot tell). Premise doesn\'t say first-round implies >700. 750 < 800 is fact. The last conclusion mixes categories — cannot directly infer.'
  }
];

export default function Syllogisms() {
  const [stage, setStage] = useState('intro');
  const [mode, setMode] = useState('easy');
  const [ucatsen, setUcatsen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [conclusionIdx, setConclusionIdx] = useState(0);
  const [conclusionPicks, setConclusionPicks] = useState([]);

  const pool = mode === 'easy' ? easyQs : hardQs;
  const q = pool[idx];

  const start = () => { setIdx(0); setAnswers([]); setPicked(null); setConclusionIdx(0); setConclusionPicks([]); setStage('drill'); };
  const nextEasy = () => {
    const correct = picked === q.answer;
    setAnswers([...answers, { ...q, given: picked, correct }]);
    setPicked(null);
    if (idx < pool.length - 1) setIdx(idx + 1);
    else setStage('done');
  };
  const nextHardConclusion = () => {
    const newPicks = [...conclusionPicks, picked];
    setConclusionPicks(newPicks);
    setPicked(null);
    if (conclusionIdx < q.conclusions.length - 1) {
      setConclusionIdx(conclusionIdx + 1);
    } else {
      const correct = newPicks.filter((p, i) => p === q.conclusions[i].answer).length;
      setAnswers([...answers, { premises: q.premises, conclusions: q.conclusions, picks: newPicks, correct, working: q.working }]);
      setConclusionPicks([]);
      setConclusionIdx(0);
      if (idx < pool.length - 1) setIdx(idx + 1);
      else setStage('done');
    }
  };

  const correctTotal = useMemo(() => {
    if (mode === 'easy') return answers.filter((a) => a.correct).length;
    return answers.reduce((sum, a) => sum + a.correct, 0);
  }, [answers, mode]);

  const totalPossible = useMemo(() => {
    if (mode === 'easy') return pool.length;
    return pool.reduce((sum, q) => sum + q.conclusions.length, 0);
  }, [pool, mode]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={drillHeader}>
        <Link to="/practice" style={drillExit}><ArrowLeft width="16" height="16" />Exit</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight /><strong style={titleStyle}>Syllogisms</strong>
        </div>
        <div style={{ width: 60 }} />
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="DM" title="Syllogisms"
          body="Two modes. Single-conclusion practice teaches the logic; the UCAT 5-statement mode mirrors real DM with partial-credit scoring."
          how={['Read the premises carefully', 'Pick True / False / Cannot tell', 'Rationales shown at the end']}
          difficulty={mode} setDifficulty={setMode}
          difficultyOptions={[
            { v: 'easy', label: 'Single conclusion', sub: 'Learn the logic · 6 questions' },
            { v: 'hard', label: 'UCAT 5-statement', sub: '3 sets × 5 conclusions · partial credit' }
          ]}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
        />
      )}

      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>
              {mode === 'easy' ? `Question ${idx + 1} of ${pool.length}` : `Set ${idx + 1} of ${pool.length} · Conclusion ${conclusionIdx + 1} of ${q.conclusions.length}`}
            </span>
          </div>

          <div className="card" style={{ marginBottom: 16, background: 'var(--card-soft)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Premises</div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {q.premises.map((p, i) => (
                <li key={i} style={{ display: 'flex', gap: 8, fontSize: 14.5, lineHeight: 1.5 }}>
                  <span style={{ color: 'var(--brand-purple)' }}>{i + 1}.</span>{p}
                </li>
              ))}
            </ul>
          </div>

          <div className="card" style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Conclusion</div>
            <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
              {mode === 'easy' ? q.q : q.conclusions[conclusionIdx].text}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[['true', 'True'], ['false', 'False'], ['cannot', "Can't tell"]].map(([v, label]) => (
                <button key={v} onClick={() => setPicked(v)} style={ratingBtn(picked === v, v)}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={mode === 'easy' ? nextEasy : nextHardConclusion} disabled={picked === null} className="btn btn-primary" style={{ opacity: picked === null ? 0.5 : 1 }}>
            {mode === 'easy'
              ? (idx === pool.length - 1 ? 'Finish' : 'Next')
              : (idx === pool.length - 1 && conclusionIdx === q.conclusions.length - 1 ? 'Finish' : 'Next')
            } <ArrowRight width="14" height="14" />
          </button>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="DM" title="Syllogisms" stats={[
          { label: 'Correct', value: `${correctTotal} / ${totalPossible}` },
          { label: 'Accuracy', value: `${Math.round((correctTotal / totalPossible) * 100)}%` },
          { label: 'Mode', value: mode === 'easy' ? 'Single conclusion' : 'UCAT 5-statement' }
        ]} onRestart={start}
          extra={<>
            <DrillResultBar slug="syllogisms" score={correctTotal} maxScore={totalPossible} accuracyPct={Math.round((correctTotal / totalPossible) * 100)} metadata={{ mode, ucatsen }} />
            {(correctTotal / totalPossible) < 0.65 && (
              <TutorNudge tutor={tutorFor('syllogisms')} reason="Syllogisms are pure pattern. James can map the 4 fallacies that catch 80% of candidates in 60 mins." kicker="Logic gaps showing" />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18, marginBottom: 22 }}>
              {answers.map((a, i) => (
                <div key={i} className="card" style={{ borderLeft: '4px solid var(--brand-purple)' }}>
                  {mode === 'easy' ? (
                    <>
                      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{a.q}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4 }}>Your: <strong>{a.given}</strong> · Correct: <strong>{a.answer}</strong></div>
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-2)', padding: '8px 12px', background: 'var(--card-soft)', borderRadius: 6 }}>{a.working}</p>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Set {i + 1} · {a.correct} / {a.conclusions.length} correct</div>
                      {a.conclusions.map((c, j) => (
                        <div key={j} style={{ fontSize: 13, marginTop: 4, color: a.picks[j] === c.answer ? 'var(--text-2)' : '#b91c1c' }}>
                          {a.picks[j] === c.answer ? '✓' : '✗'} {c.text} <span style={{ color: 'var(--text-muted)' }}>(you: {a.picks[j]} · correct: {c.answer})</span>
                        </div>
                      ))}
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-2)', padding: '8px 12px', background: 'var(--card-soft)', borderRadius: 6 }}>{a.working}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>}
        />
      )}
    </div>
  );
}

const drillHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)' };
const drillExit = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 };
const titleStyle = { fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' };

function ratingBtn(active, v) {
  const c = { true: 'var(--live-green)', false: '#b91c1c', cannot: '#b45309' }[v];
  return {
    padding: '12px 16px', textAlign: 'left', fontSize: 14.5, fontWeight: 600, cursor: 'pointer',
    border: `2px solid ${active ? c : 'var(--border)'}`,
    background: active ? `${c}15` : '#fff',
    color: active ? c : 'var(--text-2)', borderRadius: 10
  };
}
